import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Grid } from '@mui/material'
import * as yup from 'yup'

import { BaseLayout, Loader } from 'components'
import { LoginForm } from 'pages/login/components'
import {
  constants,
  deleteCookie,
  errorMsg,
  processErrorMsg,
  setCookie,
} from 'utils'
import { getAuthClient, routes } from 'config'
import { useLocation, useNavigate } from 'react-router'
import { useOktaAuth } from '@okta/okta-react'

function MultiLogin() {
  let loginPassword = ''
  const errorDate =
    window.history.state && window.history.state.state?.errorDate
  const formikRef: any = useRef()
  const [showLoader, setShowLoader] = useState(false)
  const [serviceMessage, setServiceMessage] = useState('')
  const [serviceError, setServiceError] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [transaction, setTransaction] = useState<any | {}>({})

  const location = useLocation()
  const state = location.state as { message: string }
  const message = state?.message

  const initialValues = useMemo(
    () => ({
      username: '',
      password: '',
    }),
    [],
  )
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        username: yup.string().required(errorMsg.requiredField('Login')).trim(),
        password: yup.string().required(errorMsg.requiredField('Password')),
      }),
    [],
  )
  const navigate = useNavigate()
  const { oktaAuth, authState } = useOktaAuth()

  const processSuccess = useCallback(
    (response: any) => {
      if (response.status === constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL) {
        navigate(routes.enroll)
        return null
      } else if (
        response.status === constants.OKTA_TRANSACTION_STATUS.MFA_REQUIRED
      ) {
        navigate(routes.mfa, {
          state: { loginPassword },
        })
        return null
      } else if (
        response.status ===
          constants.OKTA_TRANSACTION_STATUS.PASSWORD_EXPIRED ||
        response.status === constants.OKTA_TRANSACTION_STATUS.PASSWORD_WARN
      ) {
        // password is expiring soon (PASSWORD_WARN) or password already expired, let password-expired page handle both cases
        navigate(routes.passwordExpired, {
          state: { loginPassword },
        })
        return null
      } else if (
        response.status === constants.OKTA_TRANSACTION_STATUS.LOCKED_OUT
      ) {
        setServiceError('E0000069')
      }
      if (process.env.REACT_APP_MULTI_LOGIN === 'true') {
        setCookie('multiloginuser', 'yes', null)
      }

      setShowLoader(false)
      setTransaction(response)
    },
    [loginPassword, navigate],
  )

  const processError = useCallback(
    (e: any, module: string, moduleDescription: string = '') => {
      setShowLoader(false)
      setServiceError(processErrorMsg(e, module, moduleDescription))
    },
    [],
  )

  const handleSubmit = useCallback(
    async (values: any, { setSubmitting }: any) => {
      setSubmitting(true)

      setShowLoader(true)
      setServiceMessage('')
      setServiceError('')

      const authClient = getAuthClient()
      let signInResponse: any
      if (rememberMe === false) {
        deleteCookie('brightLoginusername')
      }

      try {
        signInResponse = await authClient.signIn({
          ...values,
          options: {
            warnBeforePasswordExpired: true,
          },
        })
      } catch (e) {
        processError(e, 'Login', 'Login Request')
        return
      }

      processSuccess(signInResponse)
      setSubmitting(false)
    },
    [processError, processSuccess, rememberMe],
  )

  const handleRemember = useCallback(() => {
    if (rememberMe) {
      setRememberMe(false)
      deleteCookie('brightLoginusername')
    } else {
      setRememberMe(true)
      setCookie('brightLoginusername', formikRef.current.values.username, 90)
    }
  }, [rememberMe])

  useEffect(() => {
    const existingUri = oktaAuth.getOriginalUri()
    if (!existingUri) {
      oktaAuth.setOriginalUri(`${routes.multiLogin}/login`)
    }
  }, [authState?.isAuthenticated, oktaAuth])

  useEffect(() => {
    if (message) {
      setServiceMessage(message)
      navigate(location.pathname, { state: '' }) // clear state
    }
  }, [navigate, location.pathname, message])

  if (oktaAuth && transaction.sessionToken) {
    oktaAuth.signInWithRedirect({ sessionToken: transaction.sessionToken })
    return null
  }

  return (
    <BaseLayout maxWidth={'74em'}>
      <Loader visible={showLoader} />
      <Grid container justifyContent="center">
        <Grid className="login-form" item xs={12} lg={5}>
          <LoginForm
            initialValues={initialValues}
            validationSchema={validationSchema}
            handleSubmit={handleSubmit}
            serviceMessage={serviceMessage}
            serviceError={serviceError}
            rememberMeValue={rememberMe}
            handleRemember={handleRemember}
            formikRef={formikRef}
            errorDate={errorDate}
            isImpersonateLogin
            title="Log in with your Multi Account"
          />
        </Grid>
      </Grid>
    </BaseLayout>
  )
}

export default MultiLogin
